/**
 * Register - 用户注册页面
 * UI-UX-Pro-Max Design System Applied
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { 
  Eye, 
  EyeOff, 
  ArrowRight,
  BookOpen,
  Sparkles,
  Gift,
  Headphones
} from 'lucide-react';
import Button from '../../components/ui/Button';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 注册福利配置
const benefits = [
  { icon: Gift, text: '新用户注册送 ¥10 体验金', color: 'text-amber-300' },
  { icon: Sparkles, text: '免费体验 3 次绘本创作', color: 'text-cyan-300' },
  { icon: Headphones, text: '专属客服一对一服务', color: 'text-pink-300' },
];

export default function Register() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { allowRegister } = useSiteConfig();
  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 如果已登录，或已关闭注册，跳转到首页
  if (isAuthenticated || allowRegister === false) {
    useEffect(() => {
      if (allowRegister === false) {
        alert('当前已关闭注册');
      }
      navigate('/', { replace: true });
    }, [allowRegister, isAuthenticated, navigate]);
    return null;
  }

  const validateForm = () => {
    if (!formData.nickname.trim()) {
      setError('请输入昵称');
      return false;
    }
    if (!formData.phone) {
      setError('请输入手机号');
      return false;
    }
    if (formData.password.length < 6) {
      setError('密码长度至少6位');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return false;
    }
    if (!formData.code) {
      setError('请输入验证码');
      return false;
    }
    if (!agreeTerms) {
      setError('请同意服务条款');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nickname: formData.nickname,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          code: formData.code,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await login(data.data.token);
        navigate('/');
      } else {
        setError(data.error || '注册失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const sendCode = async () => {
    if (countdown > 0) return;
    
    const target = formData.phone;
    if (!target) {
      setError('请先输入手机号');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          target,
          type: 'register'
        }),
      });

      const data = await response.json();
      if (data.success) {
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        // 开发环境自动填充验证码并提示
        if (data.data?.code) {
          setFormData(prev => ({ ...prev, code: data.data.code }));
          const smsMsg = data.data?.smsDetail === '短信发送成功' ? '验证码短信已发送，请注意查收。\n\n' : '';
          alert(`${smsMsg}【开发环境】验证码为：${data.data.code}\n已自动填入输入框`);
        }
      } else {
        setError(data.error || '发送失败');
      }
    } catch (err) {
      setError('发送验证码失败');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50/30 to-white flex font-body">
      {/* 左侧品牌展示 - UI-UX-Pro-Max: Exaggerated Minimalism */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center px-16 xl:px-24 h-full w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-xl w-full"
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-10">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                <BookOpen className="w-8 h-8 text-indigo-500" />
              </div>
              <span className="font-display text-4xl font-bold text-white">BadiBook</span>
            </div>

            {/* 主标题 - Exaggerated Typography */}
            <h1 className="font-display text-5xl xl:text-6xl font-bold text-white mb-6 leading-snug">
              开启孩子的<br />
              <span className="text-amber-300">AI绘本之旅</span>
            </h1>
            
            <p className="text-xl text-indigo-100 mb-12 max-w-lg leading-relaxed">
              注册即送创作体验金，让 AI 为你的孩子创作独一无二的绘本故事
            </p>

            {/* 注册福利 - 卡片式展示 */}
            <div className="space-y-4">
              {benefits.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-4 rounded-xl p-4 bg-white/10 backdrop-blur-sm border border-white/20"
                >
                  <div className={`w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center ${item.color}`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="text-lg text-white/90 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* 右侧注册表单 */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md py-8"
        >
          {/* 移动端 Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <span className="font-display text-3xl font-bold text-indigo-900">BadiBook</span>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
            <h2 className="font-display text-3xl font-bold text-indigo-900 mb-4">创建账号</h2>
            <p className="text-indigo-900/50 mb-10">填写信息开始创作之旅</p>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 昵称 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  昵称 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                  placeholder="给孩子取个可爱的昵称"
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                  required
                />
              </div>

              {/* 邮箱 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  邮箱 <span className="text-indigo-400">（选填）</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="example@email.com"
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                />
              </div>

              {/* 手机号 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  手机号 <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="请输入手机号"
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                  required
                />
              </div>

              {/* 密码 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  密码 <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="至少6位字符"
                    className="w-full px-4 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-600 transition-colors cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* 确认密码 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  确认密码 <span className="text-red-400">*</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="再次输入密码"
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                  required
                />
              </div>

              {/* 验证码 */}
              <div>
                <label className="block text-sm font-medium text-indigo-900 mb-3">
                  验证码 <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="请输入验证码"
                    className="flex-1 px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-body"
                    required
                  />
                  <button
                    type="button"
                    onClick={sendCode}
                    disabled={countdown > 0}
                    className={`px-5 py-3.5 rounded-xl font-bold transition-all whitespace-nowrap ${
                      countdown > 0
                        ? 'bg-indigo-100 text-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                    }`}
                  >
                    {countdown > 0 ? `${countdown}s` : '获取验证码'}
                  </button>
                </div>
              </div>

              {/* 服务条款 */}
              <div className="flex items-start gap-3 py-2">
                <input
                  type="checkbox"
                  id="agree"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-5 h-5 rounded-lg border-indigo-300 text-indigo-500 focus:ring-indigo-500 mt-0.5 cursor-pointer"
                />
                <label htmlFor="agree" className="text-sm text-indigo-900/70">
                  我已阅读并同意
                  <a href="#" className="text-indigo-500 hover:text-indigo-600 mx-1 font-medium">服务条款</a>
                  和
                  <a href="#" className="text-indigo-500 hover:text-indigo-600 mx-1 font-medium">隐私政策</a>
                </label>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-indigo-600 text-white rounded-xl h-12 font-semibold hover:bg-indigo-700"
                isLoading={loading}
              >
                {loading ? '注册中...' : '立即注册'}
                {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </form>

            {/* 登录入口 */}
            <div className="mt-10 text-center">
              <p className="text-indigo-900/60">
                已有账号？{' '}
                <Link to="/login" className="text-indigo-500 hover:text-indigo-600 font-bold">
                  立即登录
                </Link>
              </p>
            </div>
          </div>

          {/* 返回首页 */}
          <div className="mt-10 text-center">
            <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-500 font-medium">
              ← 返回首页
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
